import { SettingsContext } from "@/utilities/settings-context";
import { HiddenAxios } from '@/functions/axios-functions';
import { inject, reactive, provide } from '@vue/composition-api';
import { isAuthenticated } from '@/functions/authentication-functions';

export const PaymentTypes = ['monetary', 'credit'] as const;

export type PaymentType = typeof PaymentTypes[number];

export type PromotionState = 'active' | 'switch' | 'selected' | 'inactive';

export type PromotionType = "none" | 'volume' | "basic" | "boost" | "boost-plus" | 'boost-pro';

export type PackageFeatures =
  "visibility" |
  "visibility-scope" |
  "sponsored" |
  "matching-notification" |
  "logo" |
  "bumb" |
  "social-media" |
  "programattic";

export type SubPackage = "exposure" | "clicks" | "retargeting";

export type Promotions = {
  features: (PackageFeatures | [PackageFeatures, PackageFeatures[]])[];
  packages: {
    [key in Exclude<PromotionType, 'none'>]: {
      visible: boolean;
      imageSize: string;
      imageSizeInList: string;
      recommended: boolean;
      features: string[];
    }
  };
};

export const packageIds = {
  none: 0,
  volume: 1,
  basic: 2,
  boost: 3,
  'boost-plus': 4,
  'boost-pro': 5
};

const promotions = SettingsContext.get('promotions') as Readonly<Promotions>;

export const allPromotions: Exclude<PromotionType, 'none'>[] = ['volume', 'basic', 'boost', 'boost-plus', 'boost-pro'];

export const allPromotionsWithNone = ['none', 'volume', 'basic', 'boost', 'boost-plus', 'boost-pro'] as const;

export const promotionsWithCredits = ['volume', 'basic', 'boost', 'boost-plus', 'boost-pro'] as const;

export const enabledPromotions = ['basic', 'boost', 'boost-plus', 'boost-pro'] as const;

const isVolumeOrNone = (type: PromotionType): type is "none" | "volume" => type === 'volume' || type === 'none';

export const promotionHasLogo = (promotion: PromotionType) => promotion !== 'volume';

export function getPromotions() {
  return promotions;
}

export const sortPromotionType = (a: PromotionType, b: PromotionType) => {
  if (isVolumeOrNone(a) || isVolumeOrNone(b)) {
    return -1;
  }

  return promotionsWithCredits.indexOf(a) - promotionsWithCredits.indexOf(b);
};

export function getSubPackages(promotion: PromotionType): SubPackage[] {
  const promotions = getPromotions();

  if (!(promotion in promotions.packages)) {
    return [];
  }

  const plan = promotions.packages[promotion as keyof Promotions['packages']] as unknown as { features: (string | [string, string[]])[] };

  return (plan.features
    .filter((feature): feature is [string, string[]] => typeof feature !== 'string' && Array.isArray(feature) && feature[0] === promotion)
    .map(feature => feature[1])?.[0] ?? []) as SubPackage[];
}

export function isFeatureEnabledForPlan(promotion: PromotionType, feature: PackageFeatures): boolean {
  const promotions = getPromotions();

  if (!(promotion in promotions.packages)) {
    return false;
  }

  const plan = promotions.packages[promotion as keyof Promotions['packages']];

  return plan.features.includes(feature);
}

export function promotionHasSoMe(promotion?: PromotionType | null): boolean {
  return promotion === 'boost' || promotion === 'boost-plus' || promotion === 'boost-pro';
}

/**
 * Formats a number to a display string with spaces as thousand separators
 * @param number The number to format
 * @returns The formatted display string
 */
export function formatPriceDisplay(number: number): string {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function getDefaultPackagePrices(promotion: PromotionType) {
  switch (promotion) {
    case 'volume':
      return {
        price: 1000,
        priceDisplay: formatPriceDisplay(1000),
        tokenPrice: 1
      };
    case 'basic':
      return {
        price: 3900,
        priceDisplay: formatPriceDisplay(3900),
        tokenPrice: 1
      };
    case 'boost':
      return {
        price: 6900,
        priceDisplay: formatPriceDisplay(6900),
        tokenPrice: 1
      };
    case 'boost-plus':
      return {
        price: 11900,
        priceDisplay: formatPriceDisplay(11900),
        tokenPrice: 1
      };
    case 'boost-pro':
      return {
        price: 16900,
        priceDisplay: formatPriceDisplay(16900),
        tokenPrice: 1
      };
    default:
      return {
        price: 16900,
        priceDisplay: formatPriceDisplay(16900),
        tokenPrice: 1
      };
  }
}

export type PromotionPrices = {
  [key in PaymentType]: {
    [key in PromotionType]: number;
  }
}

const stateSymbol = Symbol('promotion-prices');

type PromotionStoreStateItem = {
  companyId: number;
  prices: PromotionPrices;
};

type PromotionStoreState = {
  loaded: boolean;
  loading: boolean;
  prices: PromotionStoreStateItem[];
};

const createState = () => {
  return reactive<PromotionStoreState>({
    loaded: false,
    loading: false,
    prices: [],
  });
};

export function usePromotionPrices() {
  return inject(stateSymbol) as PromotionStoreState;
}

export function providePromotionPrices() {
  return provide(
    stateSymbol,
    createState()
  );
}

export function getDefaultPromotionPrices() {
  return PaymentTypes.reduce((acc, type) => {
    acc[type] = allPromotions.reduce((acc, promotion) => {
      acc[promotion] = type === 'credit' ? getDefaultPackagePrices(promotion).tokenPrice : getDefaultPackagePrices(promotion).price;
      return acc;
    }, {} as PromotionPrices[PaymentType]);
    return acc;
  }, {} as PromotionPrices);
}

export function getPromotionPricesForCompany(companyId: number) {
  const state = usePromotionPrices();

  if (state.loaded) {
    const companyPrices = state.prices.find((item) => item.companyId === companyId);

    if (companyPrices) {
      return companyPrices.prices;
    }
  }

  return getDefaultPromotionPrices();
}

// Fetch all promotions for current user
export async function getCompaniesPricesForUser() {
  try {
    const response = await HiddenAxios.get<PromotionStoreStateItem[]>(SettingsContext.getApiUrl('getPromotionPrices'));
    return response.data;
  } catch (error) {
    console.error('Error fetching promotions:', error);
  }

  return [];
}

// Fetch promotions for a company
export async function getPackagePricesForCompany(companyId: number) {
  try {
    const response = await HiddenAxios.get<PromotionStoreStateItem>(SettingsContext.getApiUrl('getPromotionPrices') + `/${companyId}`);
    return response.data.prices;
  } catch (error) {
    console.error('Error fetching promotions:', error);
  }

  return PaymentTypes.reduce((acc, type) => {
      acc[type] = allPromotions.reduce((acc, promotion) => {
        acc[promotion] = type === 'credit' ? getDefaultPackagePrices(promotion).tokenPrice : getDefaultPackagePrices(promotion).price;
        return acc;
      }, {} as PromotionPrices[PaymentType]);
      return acc;
    }, {} as PromotionPrices);
}

export async function populatePromotionState() {
  if (!isAuthenticated()) {
    return;
  }

  const state = usePromotionPrices();

  if (state.loaded) {
    return;
  }

  if (state.loading) {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (state.loaded) {
          clearInterval(interval);
          resolve(state);
        }
      }, 1000);
    });
  }

  state.loading = true;

  try {
    const response = await getCompaniesPricesForUser();
    state.prices = response;
    state.loaded = true;
  } catch (error) {
    console.error('Error fetching promotions:', error);
  } finally {
    state.loading = false;
  }
}
